import { endOfDay, startOfDay } from "date-fns";
import { HistoryServiceListItem } from "../../../entities/history";
import { overlapsWindow, TimelineWindow, toTime } from "./timeScale";

/**
 * Prescribed services → the Timeline's swimlanes.
 *
 * Lanes follow the design's order and group `enReportServiceType` codes.
 * Lab work (6) and lab panels (8) have no lane: the labs themselves already
 * appear as dots in the Tests row.
 */
export type ServiceLaneKey =
  | "supplements"
  | "ivTherapy"
  | "medications"
  | "longevity"
  | "procedures";

export const SERVICE_LANES: {
  key: ServiceLaneKey;
  label: string;
  serviceTypes: number[];
}[] = [
  { key: "supplements", label: "Supplements", serviceTypes: [3] },
  { key: "ivTherapy", label: "IV Therapy", serviceTypes: [2] },
  // Pharmaceuticals and Pharmaceuticals IV.
  { key: "medications", label: "Medications", serviceTypes: [4, 5] },
  { key: "longevity", label: "Longevity Treatment", serviceTypes: [1] },
  { key: "procedures", label: "Medical Procedures", serviceTypes: [7] },
];

/** The design order; the user can rearrange it from the toolbar's Services picker. */
export const DEFAULT_LANE_ORDER: ServiceLaneKey[] = SERVICE_LANES.map((lane) => lane.key);

/**
 * The lanes sorted by their key's position in `order`. A key `order` does
 * not know (it never should) keeps the lanes' own, design, order and goes last.
 */
export const orderLanes = (
  lanes: TimelineServiceLane[],
  order: ServiceLaneKey[]
): TimelineServiceLane[] => {
  const rank = (lane: TimelineServiceLane) => {
    const index = order.indexOf(lane.key);
    return index === -1 ? order.length : index;
  };
  return [...lanes].sort((a, b) => rank(a) - rank(b));
};

/** `order` with the lane at `from` moved to `to`; out-of-range moves are no-ops. */
export const moveLane = (
  order: ServiceLaneKey[],
  from: number,
  to: number
): ServiceLaneKey[] => {
  if (from === to || from < 0 || to < 0 || from >= order.length || to >= order.length) {
    return order;
  }
  const next = [...order];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const laneForType = (serviceType: number) =>
  SERVICE_LANES.find((lane) => lane.serviceTypes.includes(serviceType)) ?? null;

export interface TimelineServiceBar {
  id: string;
  name: string;
  laneKey: ServiceLaneKey;
  laneLabel: string;
  startMs: number;
  endMs: number;
  /** Sub-row inside the lane, so overlapping bars never cover each other. */
  row: number;
}

export interface TimelineServiceLane {
  key: ServiceLaneKey;
  label: string;
  bars: TimelineServiceBar[];
  rowCount: number;
}

/**
 * One service → one bar, or null when it has no usable date or no lane.
 *
 * A missing `startDate` falls back to `dateCreated`. A missing `endDate`
 * makes the service a one-day bar: start to the end of that day.
 *
 * Bars cover whole days. The backend stores a wall-clock time in these dates
 * (a service prescribed "from today" starts at the moment it was saved, one
 * given an explicit date at midnight), and left as-is that put two services
 * starting the same day at different points of its column. Both ends are
 * snapped: start of the start day to end of the end day.
 *
 * `deactivationDate` cuts the bar short, the same rule as the prescription
 * period (`common/prescriptionPeriod`): deactivated before its end date, the
 * bar ends on the deactivation day; deactivated before it started, the
 * service never ran and has no bar.
 */
export const toServiceBar = (
  item: HistoryServiceListItem
): Omit<TimelineServiceBar, "row"> | null => {
  const lane = laneForType(item.serviceType);
  if (!lane) return null;
  const rawStart = toTime(item.startDate) ?? toTime(item.dateCreated);
  if (rawStart === null) return null;
  const startMs = startOfDay(rawStart).getTime();
  const deactivationMs = toTime(item.deactivationDate);
  if (deactivationMs !== null && deactivationMs < startMs) return null;
  const plannedEnd = toTime(item.endDate);
  const rawEnd =
    deactivationMs !== null && (plannedEnd === null || deactivationMs < plannedEnd)
      ? deactivationMs
      : plannedEnd;
  const endMs =
    rawEnd !== null && rawEnd >= startMs
      ? endOfDay(rawEnd).getTime()
      : endOfDay(startMs).getTime();
  return {
    id: item.id,
    name: item.serviceName || item.logicName || "Service",
    laneKey: lane.key,
    laneLabel: lane.label,
    startMs,
    endMs,
  };
};

/**
 * Greedy interval packing: earliest start first, each bar into the first
 * sub-row whose last bar has already ended.
 */
export const packBars = <T extends { startMs: number; endMs: number }>(
  bars: T[]
): { bars: (T & { row: number })[]; rowCount: number } => {
  const sorted = [...bars].sort(
    (a, b) => a.startMs - b.startMs || a.endMs - b.endMs
  );
  const rowEnds: number[] = [];
  const packed = sorted.map((bar) => {
    let row = rowEnds.findIndex((end) => end < bar.startMs);
    if (row === -1) {
      row = rowEnds.length;
      rowEnds.push(bar.endMs);
    } else {
      rowEnds[row] = bar.endMs;
    }
    return { ...bar, row };
  });
  return { bars: packed, rowCount: rowEnds.length };
};

/** The lanes with at least one bar in the window, in design order. */
export const buildServiceLanes = (
  items: HistoryServiceListItem[],
  window: TimelineWindow
): TimelineServiceLane[] => {
  const inWindow = items
    .map(toServiceBar)
    .filter(
      (bar): bar is Omit<TimelineServiceBar, "row"> =>
        bar !== null && overlapsWindow(bar.startMs, bar.endMs, window)
    );

  return SERVICE_LANES.map((lane) => {
    const { bars, rowCount } = packBars(
      inWindow.filter((bar) => bar.laneKey === lane.key)
    );
    return { key: lane.key, label: lane.label, bars, rowCount };
  }).filter((lane) => lane.bars.length > 0);
};
